import { Box } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

const normalizeDimension = (value) => (typeof value === 'number' ? `${value}px` : value);

const StyledGeneralTableBox = styled(Box, {
  shouldForwardProp: (prop) =>
    ![
      'stickyHeader',
      'showHover',
      'hasMaxHeight',
      'showZebra',
      'fixedLayout',
      'minTableWidth',
    ].includes(prop),
})(({ theme, stickyHeader, showHover, hasMaxHeight, showZebra, fixedLayout, minTableWidth }) => ({
  ...(hasMaxHeight
    ? {
        '--general-table-max-height': 'calc(100vh - 340px)',
        maxHeight: 'var(--general-table-max-height)',
        [theme.breakpoints.down('sm')]: {
          '--general-table-max-height': 'calc(100vh - 240px)',
          '.MuiDialogContent-root &': {
            '--general-table-max-height': '55vh',
          },
        },
        '.MuiDialogContent-root &': {
          '--general-table-max-height': '62vh',
        },
      }
    : {}),
  display: 'flex',
  width: '100%',
  overflowX: 'auto',
  overflowY: 'auto',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  backgroundColor: theme.palette.background.formTables,
  '& table': {
    tableLayout: fixedLayout ? 'fixed' : 'auto',
    width: fixedLayout ? '100%' : 'auto',
    minWidth: fixedLayout ? normalizeDimension(minTableWidth) || '100%' : 'max-content',
    borderCollapse: stickyHeader ? 'separate' : undefined,
    borderSpacing: stickyHeader ? 0 : undefined,
  },
  '& th, & td': fixedLayout
    ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
    : { whiteSpace: 'nowrap', width: 'auto' },
  '& :where(tbody > tr)': {
    transition: 'background-color 120ms ease',
  },
  '& :where(tbody > tr:nth-of-type(odd):not(.Mui-selected))': {
    backgroundColor: showZebra ? theme.palette.background.paper : undefined,
  },
  '& :where(tbody > tr:nth-of-type(even):not(.Mui-selected))': {
    backgroundColor: showZebra ? alpha(theme.palette.common.black, 0.055) : undefined,
  },
  '& tbody > tr:hover:not(.Mui-selected)': {
    backgroundColor: showHover
      ? `${alpha(theme.palette.info.main, 0.18)} !important`
      : undefined,
  },
  ...(stickyHeader
    ? {
        '& thead th': {
          position: 'sticky',
          top: 0,
          zIndex: 3,
          background: `linear-gradient(to bottom, ${theme.palette.primary.main} 95%, #b0b0b0 96%, #f0f0f0 100%)`,
          backgroundClip: 'padding-box',
        },
      }
    : {}),
  '*::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '*::-webkit-scrollbar-track': {
    background: 'inherit',
    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
  },
  '*::-webkit-scrollbar-thumb': {
    backgroundColor: 'gray',
    borderRadius: '20px',
  },
  '*::-webkit-scrollbar-corner': {
    background: 'inherit',
  },
}));

/**
 * Contenedor de tablas con scroll, cebra y encabezado fijo.
 * Con `fixedLayout` las columnas mantienen el ancho de su encabezado (`sx.width`) aunque
 * cambien los datos por filtros u orden; el texto que no entra se corta con puntos suspensivos.
 * @param {Object} props
 * @param {boolean} [props.fixedLayout] Anchos de columna estables.
 * @param {number|string} [props.minTableWidth] Ancho mínimo con `fixedLayout` (si no entra,
 *   scroll).
 * @returns {JSX.Element}
 */
const GeneralTableBox = ({
  children,
  sx = {},
  stickyHeader = true,
  showHover = true,
  hasMaxHeight = true,
  showZebra = true,
  maxHeight,
  fixedLayout = false,
  minTableWidth,
  ...props
}) => {
  const combinedSx = {
    ...(maxHeight !== undefined
      ? {
          '--general-table-max-height': normalizeDimension(maxHeight),
        }
      : {}),
    ...sx,
  };

  return (
    <StyledGeneralTableBox
      stickyHeader={stickyHeader}
      showHover={showHover}
      hasMaxHeight={hasMaxHeight}
      showZebra={showZebra}
      fixedLayout={fixedLayout}
      minTableWidth={minTableWidth}
      sx={combinedSx}
      {...props}
    >
      {children}
    </StyledGeneralTableBox>
  );
};

export default GeneralTableBox;
